import { siteConfig, faqs } from '@/mocks/siteData';

interface StructuredDataProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data?: Record<string, unknown>;
  breadcrumbItems?: { name: string; url: string }[];
  articleData?: {
    headline: string;
    description: string;
    image: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
    url: string;
  };
}

export default function StructuredData({ type, data, breadcrumbItems, articleData }: StructuredDataProps) {
  const generateJsonLd = () => {
    switch (type) {
      case 'Organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteConfig.name,
          url: 'https://17.cn',
          description: siteConfig.description,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '400-888-1717',
            contactType: 'customer service',
            email: 'support@17.cn',
          },
          address: {
            '@type': 'PostalAddress',
            streetAddress: '建国路88号SOHO现代城A座17层',
            addressLocality: '北京',
            addressRegion: '北京',
            postalCode: '100022',
            addressCountry: 'CN',
          },
          ...data,
        };

      case 'WebSite':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteConfig.name,
          url: 'https://17.cn',
          description: siteConfig.description,
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://17.cn/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
          ...data,
        };

      case 'WebPage': {
        const url = typeof window !== 'undefined' ? window.location.href : 'https://17.cn';
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: (data?.name as string) || siteConfig.name,
          url,
          description: (data?.description as string) || siteConfig.description,
          isPartOf: {
            '@type': 'WebSite',
            name: siteConfig.name,
            url: 'https://17.cn',
          },
          ...data,
        };
      }

      case 'BreadcrumbList':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems?.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        };

      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: articleData?.headline,
          description: articleData?.description,
          image: articleData?.image,
          datePublished: articleData?.datePublished,
          dateModified: articleData?.dateModified,
          author: {
            '@type': 'Person',
            name: articleData?.authorName,
          },
          publisher: {
            '@type': 'Organization',
            name: siteConfig.name,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': articleData?.url,
          },
        };

      case 'FAQPage':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        };

      default:
        return null;
    }
  };

  const jsonLd = generateJsonLd();

  if (!jsonLd) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}